<template>
  <div
    v-if="!loading"
    class="popup-holder popup-recording"
    :style="{ display: showRecordingPopup ? 'flex' : 'none' }"
  >
    <div class="popup-wrap">
      <div class="popup-head">
        <h2>Start New Recording</h2>
        <a @click="closePopup" href="#" class="popup-close icon-close"></a>
      </div>
      <div class="popup-body">
        <div class="recording-form">
          <div class="form-group">
            <label for="recording-title">Recording Title</label>
            <input
              v-model="recordingTitle"
              type="text"
              id="recording-title"
              class="form-control"
              placeholder="My Recording"
              maxlength="100"
              @keyup.enter="startRecording"
            />
          </div>

          <div class="form-group">
            <label for="recording-language">Transcription language</label>
            <select
              id="recording-language"
              v-model="recordingLanguage"
              class="form-control"
              @change="languageTouched = true"
            >
              <option :value="autoDetect">Auto Detect</option>
              <option
                v-for="option in languageOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div
            class="recording-info"
            v-if="!hasValidSubscription && recordingLimits"
          >
            <div
              class="limit-usage"
              v-if="recordingLimits.remainingMinutes > 0"
            >
              <div class="usage-bar">
                <div
                  class="usage-fill"
                  :style="{ width: usagePercentage + '%' }"
                ></div>
              </div>
              <div class="usage-text">
                <span class="usage-minutes"
                  >{{ recordingLimits.remainingMinutes }} minutes remaining
                  today</span
                >
                <span class="usage-details"
                  >({{ recordingLimits.usedMinutes }}/{{
                    recordingLimits.dailyLimitMinutes
                  }}
                  minutes used)</span
                >
              </div>
            </div>

            <div
              class="limit-exceeded"
              v-if="recordingLimits.remainingMinutes === 0"
            >
              <div class="info-item error">
                <span class="info-icon icon-warning"></span>
                <span class="info-text">
                  Daily recording limit reached ({{
                    recordingLimits.dailyLimitMinutes
                  }}
                  minutes)
                </span>
              </div>
            </div>

            <div class="info-item" v-if="recordingLimits.remainingFiles > 0">
              <span class="info-icon icon-info"></span>
              <span class="info-text">
                {{ recordingLimits.remainingFiles }} recording{{
                  recordingLimits.remainingFiles !== 1 ? "s" : ""
                }}
                left today
              </span>
            </div>

            <div
              class="info-item error"
              v-if="recordingLimits.remainingFiles === 0"
            >
              <span class="info-icon icon-warning"></span>
              <span class="info-text">
                Daily file limit reached ({{ recordingLimits.dailyFileLimit }}
                files)
              </span>
            </div>

            <div class="info-item">
              <span class="info-icon icon-star"></span>
              <span class="info-text">
                <a href="#" @click.prevent="handleUpgrade">Upgrade to Pro</a>
                for unlimited recordings up to 5 hours each
              </span>
            </div>
          </div>

          <!-- Active Recording Issue -->
          <div
            class="recording-info active-recording-issue"
            v-if="
              recordingLimits &&
              !recordingLimits.canRecord &&
              recordingLimits.reason?.includes('active recording')
            "
          >
            <div class="info-item error">
              <span class="info-icon icon-warning"></span>
              <span class="info-text">{{ recordingLimits.reason }}</span>
            </div>
            <button
              @click="manualCleanup"
              class="btn btn-secondary cleanup-btn"
              :disabled="isCleaningUp"
            >
              <span v-if="!isCleaningUp">Clean Up Stale Recordings</span>
              <span v-else>Cleaning...</span>
            </button>
          </div>

          <div class="recording-permissions" v-if="!hasMicrophonePermission">
            <p class="permission-text">
              We need access to your microphone to start recording.
            </p>
          </div>
        </div>
      </div>
      <div class="popup-foot">
        <button @click="closePopup" class="btn btn-cancel">Cancel</button>
        <button
          @click="startRecording"
          class="btn btn-primary"
          :disabled="isStarting || !canStartRecording"
        >
          <span v-if="!isStarting">{{ startButtonText }}</span>
          <span v-else>Starting...</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useAppStore } from "@/stores/app-store";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { mapStores } from "pinia";
import {
  initializeRecording,
  getUserRecordingLimits,
  cleanupStaleRecordings,
  cleanupActiveRecordings,
} from "@/services/recording.service";
import { useRouter } from "vue-router";
import recordingAnalytics, {
  isPermissionError,
} from "@/services/recording-analytics";
import { useLanguagePreferenceStore } from "@/stores/language-preference-store";
import { useRecordingStore } from "@/stores/recording-store";
import {
  AUTO_DETECT,
  initialLanguage,
  languageForRequest,
  languageOptions,
} from "@/constants/transcriptionLanguages";

export default {
  name: "RecordingPopup",
  props: ["showRecordingPopup"],
  async mounted() {
    try {
      // Ensure workspace store is loaded
      if (!this.workspaceStore?.subscriptionLoaded) {
        await this.workspaceStore?.fetchSubscriptionStatus();
      }
      // Fetch recording limits
      await this.fetchRecordingLimits();
    } catch (error) {
      console.error("Failed to load subscription status:", error);
    } finally {
      this.loading = false;
    }
  },
  watch: {
    showRecordingPopup(newVal) {
      // Each open or close starts a new cycle (see applyDefaultLanguage).
      this.openCycle++;
      if (newVal) {
        this.applyDefaultLanguage();
        if (!this.workspaceStore?.subscriptionLoaded) {
          this.checkSubscriptionStatus();
        }
        // Refresh limits when popup opens
        this.fetchRecordingLimits();
      }
    },
  },
  setup() {
    const router = useRouter();
    const recordingTitle = ref("");
    const isStarting = ref(false);
    const hasMicrophonePermission = ref(true);
    const loading = ref(true);
    const recordingLimits = ref(null);
    const isCleaningUp = ref(false);
    // This recording's language; starts from the saved default on open.
    const recordingLanguage = ref(AUTO_DETECT);
    // Set once the user picks a language, so a late-arriving default can't
    // overwrite their choice.
    const languageTouched = ref(false);
    // Bumped on every open and close (see applyDefaultLanguage).
    const openCycle = ref(0);

    return {
      router,
      recordingTitle,
      recordingLanguage,
      languageTouched,
      openCycle,
      autoDetect: AUTO_DETECT,
      isStarting,
      hasMicrophonePermission,
      loading,
      recordingLimits,
      isCleaningUp,
    };
  },
  computed: {
    ...mapStores(useAppStore),
    ...mapStores(useWorkSpaceStore),
    ...mapStores(useLanguagePreferenceStore),
    ...mapStores(useRecordingStore),
    languageOptions() {
      return languageOptions(this.languagePreferenceStore.defaultLanguage);
    },
    hasValidSubscription() {
      // console.log("[RecordingPopup] Checking subscription:", {
      //   workspaceStore: this.workspaceStore,
      //   isSubscribedState: this.workspaceStore?.isSubscribedState,
      //   subscriptionLoaded: this.workspaceStore?.subscriptionLoaded,
      // });
      return this.workspaceStore?.isSubscribedState || false;
    },
    usagePercentage() {
      if (!this.recordingLimits) return 0;
      return Math.min(
        100,
        (this.recordingLimits.usedMinutes /
          this.recordingLimits.dailyLimitMinutes) *
          100,
      );
    },
    canStartRecording() {
      if (!this.recordingLimits) return true; // Allow if we haven't loaded limits yet
      return this.recordingLimits.canRecord;
    },
    startButtonText() {
      if (!this.canStartRecording && this.recordingLimits) {
        return "Limit Reached";
      }
      return "Start Recording";
    },
  },
  methods: {
    async checkSubscriptionStatus() {
      try {
        this.loading = true;
        if (!this.workspaceStore?.subscriptionLoaded) {
          await this.workspaceStore?.fetchSubscriptionStatus();
        }
      } catch (error) {
        console.error("Failed to check subscription status:", error);
      } finally {
        this.loading = false;
      }
    },

    closePopup() {
      this.appStore.showRecordingPopup = false;
      this.recordingTitle = "";
      this.isStarting = false;
    },

    // Preselect the saved default (Auto-detect if none) for this recording.
    async applyDefaultLanguage() {
      const cycle = this.openCycle;
      this.languageTouched = false;
      this.recordingLanguage = initialLanguage(
        this.languagePreferenceStore.defaultLanguage,
      );
      const saved = await this.languagePreferenceStore.load();
      // Not once the popup has closed or reopened, and not over the user's
      // own pick.
      if (cycle === this.openCycle && !this.languageTouched) {
        this.recordingLanguage = initialLanguage(saved);
      }
    },

    async fetchRecordingLimits() {
      try {
        const { data, error } = await getUserRecordingLimits();
        if (error) {
          console.error("Failed to fetch recording limits:", error);
          return;
        }

        // Clear existing data first to trigger reactivity
        this.recordingLimits = null;
        await this.$nextTick();

        // Then set new data
        this.recordingLimits = data;
        console.log(
          "[RecordingPopup] Recording limits updated:",
          this.recordingLimits,
        );

        // If can't record due to active session, try to cleanup stale recordings
        if (!data.canRecord && data.reason?.includes("active recording")) {
          console.log(
            "[RecordingPopup] Found active recording, attempting cleanup...",
          );
          await this.tryCleanupStaleRecordings();
        }
      } catch (error) {
        console.error("Failed to fetch recording limits:", error);
      }
    },

    async tryCleanupStaleRecordings() {
      try {
        const { data: cleanupData, error: cleanupError } =
          await cleanupStaleRecordings();

        if (cleanupError) {
          console.error("Failed to cleanup stale recordings:", cleanupError);
          return null;
        }

        console.log("[RecordingPopup] Cleanup result:", cleanupData);

        if (cleanupData.cleaned && cleanupData.count > 0) {
          this.$toast.success(
            `Cleaned up ${cleanupData.count} stale recording(s). You can now start a new recording.`,
          );

          // Refresh limits after cleanup
          await this.fetchRecordingLimits();
        }

        return cleanupData;
      } catch (error) {
        console.error("Failed to cleanup stale recordings:", error);
        return null;
      }
    },

    async manualCleanup() {
      this.isCleaningUp = true;
      try {
        // First try to clean up stale recordings (older than 1 hour)
        let cleanupResult = await this.tryCleanupStaleRecordings();

        // If no stale recordings were found, try to clean up ALL active recordings
        if (!cleanupResult || cleanupResult.count === 0) {
          console.log(
            "[RecordingPopup] No stale recordings found, trying to clean up all active recordings...",
          );

          const { data: activeCleanupData, error: activeCleanupError } =
            await cleanupActiveRecordings();

          if (activeCleanupError) {
            console.error(
              "Failed to cleanup active recordings:",
              activeCleanupError,
            );
            this.$toast.error(
              "Failed to clean up active recordings. Please try again.",
            );
            return;
          }

          console.log(
            "[RecordingPopup] Active cleanup result:",
            activeCleanupData,
          );

          if (activeCleanupData.cleaned && activeCleanupData.count > 0) {
            this.$toast.success(
              `Cleaned up ${activeCleanupData.count} active recording(s). You can now start a new recording.`,
            );

            // Refresh limits after cleanup - clear first to ensure reactivity
            this.recordingLimits = null;
            await this.$nextTick();
            await this.fetchRecordingLimits();
          } else {
            this.$toast.info("No active recordings found to clean up.");
          }
        }
      } finally {
        this.isCleaningUp = false;
      }
    },

    handleUpgrade() {
      this.closePopup();
      this.router.push("/settings?tab=subscription");
    },

    async checkMicrophonePermission() {
      try {
        recordingAnalytics.permissionRequested();
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());
        recordingAnalytics.permissionGranted();
        this.hasMicrophonePermission = true;
        return true;
      } catch (error) {
        console.error("Microphone permission denied:", error);
        // Classify: getUserMedia also rejects with NotFoundError (no mic),
        // NotReadableError (device busy) and AbortError. Counting those as
        // denials would corrupt the permission funnel with hardware failures.
        if (isPermissionError(error)) {
          recordingAnalytics.permissionDenied(error);
        } else {
          recordingAnalytics.failed(null, {
            error,
            phase: "init",
            code: "getusermedia_failed",
            durationBeforeFailure: 0,
          });
        }
        this.hasMicrophonePermission = false;
        return false;
      }
    },

    async startRecording() {
      // The Enter key reaches here even while the button is disabled.
      if (this.isStarting) return;
      console.log("[RecordingPopup] Starting recording...");
      this.isStarting = true;
      // Read once: the picker (or a late-loading default) can change while
      // we wait below, and /initialize and the live preview must agree.
      const selectedLanguage = this.recordingLanguage;

      try {
        // The workspace store starts fetching in the background as soon as
        // it's created (not awaited there), so a fast initial page load can
        // reach this popup before that resolves — activeWorkspaceUUID
        // defaults to "" until then. Await the store's OWN in-flight load
        // (workspacesReady) rather than sending an empty value or firing a
        // second, redundant fetchWorkspaces() call: the backend treats a
        // present-but-empty workspaceUUID as an explicit (invalid) workspace
        // and 404s, which is worse than the old default-workspace fallback
        // this fix is replacing.
        if (!this.workspaceStore.workspacesLoaded) {
          await this.workspaceStore.workspacesReady;
        }
        const workspaceUUID = this.workspaceStore.activeWorkspaceUUID;
        if (!workspaceUUID) {
          console.error("[RecordingPopup] No active workspace available");
          this.$toast.error(
            "Unable to determine your workspace. Please refresh and try again.",
          );
          this.isStarting = false;
          return;
        }

        // Check microphone permission
        console.log("[RecordingPopup] Checking microphone permission...");
        const hasPermission = await this.checkMicrophonePermission();
        if (!hasPermission) {
          console.error("[RecordingPopup] Microphone permission denied");
          this.isStarting = false;
          return;
        }

        // Initialize recording on backend
        console.log("[RecordingPopup] Initializing recording on backend...");
        const { data, error } = await initializeRecording(
          this.recordingTitle || "Untitled Recording",
          {
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            // Without this, the backend falls back to the user's first/default
            // workspace (mirrors iOS, which already sends it this way) — a
            // recording started from any other workspace would silently land
            // in the wrong one.
            workspaceUUID,
          },
          languageForRequest(
            selectedLanguage,
            this.languagePreferenceStore.defaultLanguage,
          ),
        );

        console.log("[RecordingPopup] Initialize response:", data, error);

        if (error) {
          // Extract the specific error message from the backend
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to initialize recording";
          recordingAnalytics.failed(null, {
            error,
            phase: "init",
            code: "backend_initialize_failed",
            durationBeforeFailure: 0,
          });
          const envelopeError = new Error(errorMessage);
          envelopeError.alreadyTracked = true;
          throw envelopeError;
        }

        const { audioFileId, audioFileUUID } = data;
        console.log("[RecordingPopup] Recording initialized:", {
          audioFileId,
          audioFileUUID,
        });

        // The recording view picks the live-transcription model from this.
        this.recordingStore.rememberLiveLanguage(audioFileId, selectedLanguage);

        // Close popup and navigate to recording view
        this.closePopup();
        console.log("[RecordingPopup] Navigating to recording view...");
        this.router.push(`/record/${audioFileId}`);
      } catch (error) {
        console.error("Failed to start recording:", error);
        // Reachable without prior telemetry when initializeRecording THROWS
        // rather than returning an error envelope (e.g. token acquisition
        // fails). `alreadyTracked` is set at the envelope-error site so this
        // does not double-count that path.
        if (!error.alreadyTracked) {
          recordingAnalytics.failed(null, {
            error,
            phase: "init",
            code: "start_recording_failed",
            durationBeforeFailure: 0,
          });
        }
        // Show the specific error message from the backend
        const errorMessage =
          error.message || "Failed to start recording. Please try again.";
        this.$toast.error(errorMessage);
        this.isStarting = false;

        // Refresh limits in case they've changed
        await this.fetchRecordingLimits();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:color";

.popup-recording {
  .popup-wrap {
    width: 480px;
    max-width: 90vw;
  }

  .recording-form {
    .form-group {
      margin-bottom: 20px;

      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: $gray;
      }

      .form-control {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid $gray100;
        border-radius: 6px;
        font-size: 14px;

        &:focus {
          outline: none;
          border-color: $primary;
        }
      }
    }

    .recording-info {
      background-color: $gray600;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 20px;

      &.active-recording-issue {
        background-color: color.adjust($danger, $lightness: 40%);
        border: 1px solid $danger;

        .cleanup-btn {
          margin-top: 12px;
          width: 100%;
          background-color: $danger;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;

          &:hover:not(:disabled) {
            background-color: color.adjust($danger, $lightness: -10%);
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }

      .limit-usage {
        margin-bottom: 16px;

        .usage-bar {
          height: 8px;
          background-color: $gray100;
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 8px;

          .usage-fill {
            height: 100%;
            background-color: $primary;
            transition: width 0.3s ease;
          }
        }

        .usage-text {
          display: flex;
          justify-content: space-between;
          font-size: 14px;

          .usage-minutes {
            color: $gray;
            font-weight: 500;
          }

          .usage-details {
            color: $gray400;
          }
        }
      }

      .limit-exceeded {
        margin-bottom: 16px;
      }

      .info-item {
        display: flex;
        align-items: flex-start;
        gap: 10px;

        &:not(:last-child) {
          margin-bottom: 12px;
        }

        &.error {
          .info-icon {
            color: $danger;
          }

          .info-text {
            color: $danger;
            font-weight: 500;
          }
        }

        .info-icon {
          flex-shrink: 0;
          color: $gray400;
          font-size: 16px;
          margin-top: 2px;
        }

        .info-text {
          font-size: 14px;
          color: $gray400;
          line-height: 1.5;

          a {
            color: $primary;
            text-decoration: none;
            font-weight: 500;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }

    .recording-permissions {
      background-color: color.adjust($primary, $lightness: 40%);
      border: 1px solid $primary;
      border-radius: 6px;
      padding: 12px 16px;

      .permission-text {
        margin: 0;
        font-size: 14px;
        color: color.adjust($primary, $lightness: -10%);
      }
    }
  }

  .popup-foot {
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .btn-cancel {
      background-color: transparent;
      color: $gray400;

      &:hover {
        background-color: $gray600;
      }
    }

    .btn-primary {
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;

        &:hover {
          background-color: $primary; // Don't change color on hover when disabled
        }
      }
    }
  }
}
</style>
